import React, { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { printChecklistReport } from "@/lib/printChecklistReport";
import {
  FileText, Upload, Link2, Trash2, ExternalLink, Plus, Loader2, FileCheck, AlertTriangle, Mail,
} from "lucide-react";

const STATES = [
  { key: "pending", label: "Pending" },
  { key: "received", label: "Received" },
  { key: "na", label: "N/A" },
];

function StatusControl({ value, onChange, disabled }) {
  return (
    <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden shrink-0" data-testid="doc-status-control">
      {STATES.map((s) => {
        const on = value === s.key;
        const tone = s.key === "received" ? "bg-emerald-600" : s.key === "na" ? "bg-zinc-500" : "bg-amber-500";
        return (
          <button key={s.key} disabled={disabled} onClick={() => onChange(s.key)}
            data-testid={`doc-status-${s.key}`}
            className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${on ? `${tone} text-white` : "bg-white text-zinc-500 hover:bg-zinc-50"} ${s.key !== "pending" ? "border-l border-zinc-200" : ""}`}>
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

const CONSULT_STATUS_LABEL = {
  needed: "Needed", quote_requested: "Quote requested", quote_received: "Quote received",
  engaged: "Engaged", report_received: "Report received", invoice_paid: "Invoice paid", not_required: "Not required",
};
const SERVICE_KW = {
  "Bushfire (BAL)": ["bushfire"], "Acoustic": ["acoustic"], "Flood": ["flood"],
  "Arborist": ["arborist", "tree"], "Landscape": ["landscape"], "Heritage": ["heritage"],
  "Geotechnical": ["geotech"], "Quantity surveyor": ["quantity", "cost report"],
  "Stormwater / civil / OSD": ["stormwater", "civil", "osd"], "Structural": ["structural"],
  "Surveyor": ["survey"], "Sewer pegout / service protection": ["sewer", "pegout"],
  "Sydney Water WSC / BPA": ["sydney water", "wsc", "bpa"],
};

export default function DocumentChecklist({ projectId, project, checklist, canManage, onReload }) {
  const items = checklist?.items || [];
  const [drafts, setDrafts] = useState({});
  const [busyItem, setBusyItem] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [linkFor, setLinkFor] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [condDefs, setCondDefs] = useState([]);
  const [conditions, setConditions] = useState({});
  const [pathway, setPathway] = useState("none");
  const [consultRows, setConsultRows] = useState([]);
  const fileInputs = useRef({});

  const loadConsult = () => api.get(`/projects/${projectId}/consultants`).then((r) => setConsultRows(r.data.rows)).catch(() => {});
  useEffect(() => {
    api.get(`/projects/${projectId}/checklist/site-conditions`).then((r) => {
      setCondDefs(r.data.definitions || []);
      setConditions(r.data.conditions || {});
      setPathway(r.data.approval_pathway || "none");
    }).catch(() => {});
    loadConsult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const saveConditions = async (nextConditions, nextPathway) => {
    try {
      await api.put(`/projects/${projectId}/checklist/site-conditions`, {
        conditions: nextConditions, approval_pathway: nextPathway === "none" ? null : nextPathway,
      });
      loadConsult(); onReload && (await onReload());
    } catch (e) { toast.error(apiErr(e, "Could not update site conditions")); }
  };
  const setCondition = (key, val) => { const n = { ...conditions, [key]: val }; setConditions(n); saveConditions(n, pathway); };
  const setPath = (val) => { setPathway(val); saveConditions(conditions, val); };

  const consultFor = (label) => {
    const l = (label || "").toLowerCase();
    return consultRows.find((row) => (SERVICE_KW[row.service] || []).some((k) => l.includes(k))) || null;
  };

  const applicable = items.filter((i) => i.status !== "na");
  const received = applicable.filter((i) => i.status === "received").length;
  const missing = applicable.filter((i) => i.status === "pending");

  const call = async (fn, msg) => {
    try { await fn(); onReload && (await onReload()); }
    catch (e) { toast.error(apiErr(e, msg)); }
  };

  const setStatus = (item, status) =>
    call(() => api.patch(`/projects/${projectId}/checklist/items/${item.item_id}`, { status }), "Could not update status");

  const saveNotes = (item) => {
    const draft = drafts[item.item_id];
    if (draft === undefined || draft === (item.notes || "")) return;
    call(() => api.patch(`/projects/${projectId}/checklist/items/${item.item_id}`, { notes: draft }), "Could not save notes")
      .then(() => setDrafts((d) => { const n = { ...d }; delete n[item.item_id]; return n; }));
  };

  const uploadFile = async (item, file) => {
    if (!file) return;
    setBusyItem(item.item_id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/projects/${projectId}/checklist/items/${item.item_id}/attachment`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      onReload && (await onReload());
    } catch (e) { toast.error(apiErr(e, "Upload failed")); }
    finally { setBusyItem(null); }
  };

  const addLink = (item) => {
    const url = linkUrl.trim();
    if (!url) return;
    call(() => api.post(`/projects/${projectId}/checklist/items/${item.item_id}/link`, { url }), "Could not add link")
      .then(() => { setLinkFor(null); setLinkUrl(""); });
  };

  const removeAtt = (item, att) =>
    call(() => api.delete(`/projects/${projectId}/checklist/items/${item.item_id}/attachment/${att.att_id}`), "Could not remove");

  const openAtt = async (att) => {
    if (att.type === "link") { window.open(att.url, "_blank"); return; }
    try {
      const blob = (await api.get(`/files/${att.storage_path}`, { responseType: "blob" })).data;
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) { toast.error("Could not open file"); }
  };

  const addItem = () => {
    const label = newLabel.trim();
    if (!label) return;
    call(() => api.post(`/projects/${projectId}/checklist/items`, { label }), "Could not add item")
      .then(() => setNewLabel(""));
  };

  const removeItem = (item) =>
    call(() => api.delete(`/projects/${projectId}/checklist/items/${item.item_id}`), "Could not remove item");

  const disableChecklist = () =>
    call(() => api.put(`/projects/${projectId}/checklist/enable`, { enabled: false }), "Could not disable");

  const report = () => {
    const ok = printChecklistReport({ project, checklist });
    if (ok === false) toast.error("Allow pop-ups to generate the report");
  };

  const openEmail = () => {
    setEmailTo(project?.client_details?.email || "");
    setEmailOpen(true);
  };

  const emailReport = async () => {
    const to = emailTo.trim();
    if (!to) { toast.error("Enter a recipient email"); return; }
    setEmailing(true);
    try {
      const r = await api.post(`/projects/${projectId}/checklist/email`, { to });
      toast.success(`Report emailed to ${r.data.to}`);
      setEmailOpen(false);
    } catch (e) { toast.error(apiErr(e, "Could not email the report")); }
    finally { setEmailing(false); }
  };

  return (
    <div className="bg-white border border-zinc-200" data-testid="document-checklist">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-zinc-500" />
          <div>
            <h3 className="font-heading font-bold">Pre-Design Document Checklist</h3>
            <p className="text-xs text-zinc-500">
              {received}/{applicable.length} received
              {missing.length > 0
                ? <span className="text-rose-600 font-semibold"> · {missing.length} missing</span>
                : <span className="text-emerald-600 font-semibold"> · complete</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="generate-report-button" onClick={report} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-2">
            <FileCheck className="h-4 w-4" /> Generate Report
          </Button>
          {canManage && (
            <Button variant="outline" onClick={openEmail} className="rounded-sm gap-2" data-testid="email-report-button">
              <Mail className="h-4 w-4" /> Email to client
            </Button>
          )}
          {canManage && (
            <Button variant="outline" onClick={disableChecklist} className="rounded-sm text-xs" data-testid="disable-checklist">
              Turn off
            </Button>
          )}
        </div>
      </div>

      {missing.length > 0 && (
        <div className="flex items-start gap-2 px-5 py-3 bg-rose-50 border-b border-rose-100 text-sm text-rose-700" data-testid="missing-banner">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span><strong>{missing.length} missing:</strong> {missing.map((i) => i.label).join(", ")}</span>
        </div>
      )}

      {/* Site conditions → drives the Consultants tab */}
      <div className="px-5 py-4 border-b border-zinc-200 bg-zinc-50/60" data-testid="site-conditions">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h4 className="font-heading font-bold text-sm">Site conditions</h4>
            <p className="text-xs text-zinc-500">Setting a condition to <strong>Yes</strong> adds the matching consultant(s) to the Consultants tab.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Approval pathway</span>
            <Select value={pathway} onValueChange={setPath} disabled={!canManage}>
              <SelectTrigger className="h-8 w-[130px] rounded-sm text-xs" data-testid="approval-pathway"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not set</SelectItem>
                <SelectItem value="da">DA (Town planner)</SelectItem>
                <SelectItem value="cdc">CDC (Certifier)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          {condDefs.map((c) => {
            const val = conditions[c.key] || "unknown";
            return (
              <div key={c.key} className="flex items-center justify-between gap-3 py-1" data-testid={`condition-${c.key}`}>
                <span className="text-sm text-zinc-700 min-w-0">{c.label}</span>
                <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden shrink-0">
                  {["yes", "no", "unknown"].map((opt) => {
                    const on = val === opt;
                    const tone = opt === "yes" ? "bg-rose-600" : opt === "no" ? "bg-zinc-600" : "bg-amber-500";
                    return (
                      <button key={opt} disabled={!canManage} onClick={() => setCondition(c.key, opt)}
                        data-testid={`condition-${c.key}-${opt}`}
                        className={`px-2.5 py-1 text-[11px] font-semibold capitalize transition-colors ${on ? `${tone} text-white` : "bg-white text-zinc-500 hover:bg-zinc-50"} ${opt !== "yes" ? "border-l border-zinc-200" : ""}`}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="divide-y divide-zinc-100">
        {items.map((item) => (
          <div key={item.item_id} className="px-5 py-4" data-testid={`doc-item-${item.item_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm flex items-center gap-2">
                  {item.label}
                  {item.kind === "other" && <span className="text-[9px] uppercase tracking-wider text-zinc-400 border border-zinc-200 rounded-sm px-1">custom</span>}
                </p>
                {(() => {
                  const cr = consultFor(item.label);
                  if (!cr) return null;
                  return (
                    <p className="text-[11px] text-indigo-700 mt-0.5" data-testid={`doc-consult-status-${item.item_id}`}>
                      {cr.service} – {CONSULT_STATUS_LABEL[cr.status] || cr.status}
                      {cr.consultant_name ? `: ${cr.consultant_name}` : ""}
                      {cr.date_requested ? ` · requested ${new Date(cr.date_requested + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short" })}` : ""}
                    </p>
                  );
                })()}
                {item.updated_by_name && (
                  <p className="text-[10px] text-zinc-400 mt-0.5">
                    Updated by {item.updated_by_name}{item.updated_at ? ` · ${new Date(item.updated_at).toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "2-digit", timeZone: "Australia/Sydney" })}` : ""}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <StatusControl value={item.status} onChange={(s) => setStatus(item, s)} disabled={busyItem === item.item_id} />
                {item.kind === "other" && (
                  <button onClick={() => removeItem(item)} className="text-zinc-400 hover:text-rose-600 transition-colors" data-testid={`remove-item-${item.item_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* attachments */}
            <div className="flex flex-wrap gap-2 mt-3">
              {(item.attachments || []).map((a) => (
                <span key={a.att_id} className="inline-flex items-center gap-1.5 bg-zinc-100 rounded-sm pl-2 pr-1 py-1 text-xs" data-testid={`attachment-${a.att_id}`}>
                  {a.type === "link" ? <Link2 className="h-3 w-3 text-zinc-500" /> : <FileText className="h-3 w-3 text-zinc-500" />}
                  <button onClick={() => openAtt(a)} className="max-w-[180px] truncate hover:underline">{a.type === "link" ? (a.name || a.url) : a.original_filename}</button>
                  <button onClick={() => openAtt(a)} className="text-zinc-400 hover:text-zinc-900"><ExternalLink className="h-3 w-3" /></button>
                  <button onClick={() => removeAtt(item, a)} className="text-zinc-400 hover:text-rose-600" data-testid={`remove-attachment-${a.att_id}`}><Trash2 className="h-3 w-3" /></button>
                </span>
              ))}
              <input ref={(el) => (fileInputs.current[item.item_id] = el)} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                onChange={(e) => { uploadFile(item, e.target.files[0]); e.target.value = ""; }} data-testid={`file-input-${item.item_id}`} />
              <button onClick={() => fileInputs.current[item.item_id]?.click()} disabled={busyItem === item.item_id}
                className="inline-flex items-center gap-1 text-xs text-zinc-500 border border-dashed border-zinc-300 rounded-sm px-2 py-1 hover:border-zinc-500 hover:text-zinc-800 transition-colors"
                data-testid={`upload-file-${item.item_id}`}>
                {busyItem === item.item_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} File
              </button>
              <button onClick={() => { setLinkFor(linkFor === item.item_id ? null : item.item_id); setLinkUrl(""); }}
                className="inline-flex items-center gap-1 text-xs text-zinc-500 border border-dashed border-zinc-300 rounded-sm px-2 py-1 hover:border-zinc-500 hover:text-zinc-800 transition-colors"
                data-testid={`add-link-${item.item_id}`}>
                <Link2 className="h-3 w-3" /> Link
              </button>
            </div>
            {linkFor === item.item_id && (
              <div className="flex gap-2 mt-2">
                <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://drive.google.com/…" autoFocus
                  className="rounded-sm h-8 text-sm" data-testid={`link-input-${item.item_id}`}
                  onKeyDown={(e) => e.key === "Enter" && addLink(item)} />
                <Button size="sm" className="h-8 rounded-sm bg-zinc-900 hover:bg-zinc-800" onClick={() => addLink(item)} data-testid={`save-link-${item.item_id}`}>Add</Button>
              </div>
            )}

            {/* notes */}
            <Textarea
              value={drafts[item.item_id] !== undefined ? drafts[item.item_id] : (item.notes || "")}
              onChange={(e) => setDrafts((d) => ({ ...d, [item.item_id]: e.target.value }))}
              onBlur={() => saveNotes(item)}
              placeholder="Notes — easements, covenants, BAL rating, acoustic requirements, design controls…"
              className="mt-3 rounded-sm text-sm min-h-[60px]" data-testid={`notes-${item.item_id}`} />
          </div>
        ))}
      </div>

      {/* add custom item */}
      <div className="flex gap-2 px-5 py-4 border-t border-zinc-100">
        <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Add another document (e.g. Survey Report)…"
          className="rounded-sm h-9 text-sm" data-testid="new-item-input" onKeyDown={(e) => e.key === "Enter" && addItem()} />
        <Button onClick={addItem} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-1 shrink-0" data-testid="add-item-button">
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="email-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Email Site Constraints Report</DialogTitle>
            <DialogDescription>Send the report as an email. Edit the recipient if needed.</DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Recipient email</label>
            <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
              placeholder="client@example.com" className="mt-1.5 rounded-sm" autoFocus
              data-testid="email-recipient-input" onKeyDown={(e) => e.key === "Enter" && emailReport()} />
            {!project?.client_details?.email && (
              <p className="text-xs text-amber-600 mt-1.5">No email on the client folder — enter one to send.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-2" onClick={emailReport} disabled={emailing} data-testid="send-report-email">
              <Mail className="h-4 w-4" /> {emailing ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
